// CheckoutForm.tsx
import { useRef } from "react"
import { Control, Controller, FieldErrors } from "react-hook-form"

import FancyText from "@fuse/sharedComponents/typography/FancyText"
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from "@stripe/react-stripe-js"

import CreditCardFrontFill from "@fuse/assets/images/eventSignUp/img-alpha-credit-card-front-fill.svg"

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#FFFFFF",
      fontFamily: '"Open Sans", "Helvetica Neue", "Helvetica", sans-serif',
      fontSize: "16px",
      "::placeholder": {
        color: "rgba(255, 255, 255, 0.30)",
      },
    },
    invalid: {
      color: "#D73A3A",
      iconColor: "#D73A3A",
    },
  },
}

const cardNumberElementOptions = {
  ...CARD_ELEMENT_OPTIONS,
  placeholder: "0000 0000 0000 0000",
}

const cardExpiryElementOptions = {
  ...CARD_ELEMENT_OPTIONS,
  placeholder: "MM/YY",
}

const cardCvcElementOptions = {
  ...CARD_ELEMENT_OPTIONS,
  placeholder: "CVV",
}

interface CheckoutFormProps {
  control: Control<any>
  errors: FieldErrors<any>
}

const CheckoutForm = ({ control, errors }: CheckoutFormProps) => {
  const cardNumberRef = useRef(null)
  const cardExpiryRef = useRef(null)
  const cardCvcRef = useRef(null)

  return (
    <>
      <div className="max-w-sm md:max-w-lg w-full mt-4">
        <div className="flex w-full flex-col">
          <FancyText className="text-white text-sm text-left">
            Card details
          </FancyText>

          <div className="flex w-full flex-row mt-2 bg-white/10 rounded-md p-4">
            <div className="flex items-center justify-center">
              <img src={CreditCardFrontFill} alt="First" />
            </div>
            <Controller
              control={control}
              name="cardNumber"
              render={({ field: { onChange, onBlur, value } }) => (
                <CardNumberElement
                  // ref={cardNumberRef}
                  options={cardNumberElementOptions}
                  // value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  className="w-full text-xs md:text-base pl-4 placeholder-white/30 text-white border-0 focus:outline-none font-[400] leading-tight"
                />
              )}
            />
          </div>
          {errors["cardNumber"] && (
            <FancyText className="text-xs md:text-base text-textError font-normal text-left">
              {errors["cardNumber"]?.message?.toString()}
            </FancyText>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 max-w-sm md:max-w-lg w-full mt-4">
        <div className="flex w-full flex-col">
          <Controller
            control={control}
            name="expiry"
            render={({ field: { onChange, onBlur, value } }) => (
              <CardExpiryElement
                // ref={cardExpiryRef}
                options={cardExpiryElementOptions}
                // value={value}
                onChange={onChange}
                onBlur={onBlur}
                className="text-xs md:text-base bg-white/10 p-4 rounded-md placeholder-white/30 text-white border-0 focus:outline-none font-[400] leading-tight mt-2"
              />
            )}
          />
          {errors["expiry"] && (
            <FancyText className="text-xs md:text-base text-textError font-normal text-left">
              {errors["expiry"]?.message?.toString()}
            </FancyText>
          )}
        </div>

        <div className="flex w-full flex-col">
          <Controller
            control={control}
            name="cvv"
            render={({ field: { onChange, onBlur, value } }) => (
              <CardCvcElement
                // ref={cardCvcRef}
                options={cardCvcElementOptions}
                // value={value}
                onChange={onChange}
                onBlur={onBlur}
                className="text-xs md:text-base bg-white/10 p-4 rounded-md placeholder-white/30 text-white border-0 focus:outline-none font-[400] leading-tight mt-2"
              />
            )}
          />
          {errors["cvv"] && (
            <FancyText className="text-xs md:text-base text-textError font-normal text-left">
              {errors["cvv"]?.message?.toString()}
            </FancyText>
          )}
        </div>

        <div className="flex w-full flex-col">
          <Controller
            control={control}
            name="zipCode"
            render={({ field: { onChange, onBlur, value } }) => (
              <input
                type="text"
                name="zipCode"
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder="Zip code"
                className="text-[16px] bg-white/10 p-4 rounded-md placeholder-white/30 text-white border-0 focus:outline-none font-[400] leading-tight mt-2"
              />
            )}
          />
          {errors["zipCode"] && (
            <FancyText className="text-xs md:text-base text-textError font-normal text-left">
              {errors["zipCode"]?.message?.toString()}
            </FancyText>
          )}
        </div>
      </div>
    </>
  )
}

export default CheckoutForm
